import React, { useCallback, useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { useLocation, useSearchParams } from "react-router-dom"
import { toast, Bounce } from "react-toastify"
import PageHeader from "../../components/DashboardComponents/PageHeader"
import ListingTabs from "../../components/Listing/ListingTabs"
import SearchInput from "../../components/Listing/SearchInput"
import FilterDropdown from "../../components/Listing/FilterDropdown"
import Pagination from "../../components/Listing/Pagination"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../components/Common/EmptyState"
import TruncatedText from "../../components/Common/TruncatedText"
import { ICONS } from "../../constants/icons"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"

const PAGE_SIZE = 10
const SKELETON_ROWS = 10
const SEARCH_DEBOUNCE_MS = 300

const MODULE_META = {
  profile: {
    tab: "profile",
    label: "My Profile",
    tone: "blue",
    icon: ICONS.activityProfile,
  },
  account: {
    tab: "profile",
    label: "Account",
    tone: "gray",
    icon: ICONS.activitySecurity,
  },
  projects: {
    tab: "projects",
    label: "Projects",
    tone: "green",
    icon: ICONS.activityDocumentText,
  },
  invites: {
    tab: "projects",
    label: "Invites",
    tone: "purple",
    icon: ICONS.activitySmsTracking,
  },
  sessions: {
    tab: "projects",
    label: "Sessions",
    tone: "blue",
    icon: ICONS.activityClock,
  },
  calls: {
    tab: "projects",
    label: "Calls for Artists",
    tone: "orange",
    icon: ICONS.activityTask,
  },
  messages: {
    tab: "messages",
    label: "Messages",
    tone: "blue",
    icon: ICONS.activityMessage,
  },
  flags: {
    tab: "flags",
    label: "Safety",
    tone: "red",
    icon: ICONS.activityFlag,
  },
  rewards: {
    tab: "rewards",
    label: "Treasure & XP",
    tone: "yellow",
    icon: ICONS.activityAward,
  },
  photography: {
    tab: "profile",
    label: "Photography",
    tone: "yellow",
    icon: ICONS.activityCamera,
  },
  blogs: {
    tab: "profile",
    label: "Blogs",
    tone: "purple",
    icon: ICONS.activityDocumentText,
  },
}

const SORT_OPTIONS = [
  { value: "created_at", label: "Date & Time" },
  { value: "id", label: "ID" },
]

const DEFAULT_SORT_BY = "created_at"

const TAB_KEYS = ["all", "user", "project", "message", "flag", "reward"]

const TAB_LABELS = {
  all: "All",
  user: "User",
  project: "Project",
  message: "Message",
  flag: "Flag",
  reward: "Treasure",
}

/** FilterDropdown options — API subject_type values. */
const SUBJECT_TYPE_FILTER_OPTIONS = [
  { value: "user", label: "User" },
  { value: "project", label: "Project" },
  { value: "message", label: "Message" },
  { value: "flag", label: "Flag" },
  { value: "reward", label: "Treasure" },
]

const stripHtml = value =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()


const SUBJECT_MODULE = {
  project_task: "projects",
  project: "projects",
  project_member: "invites",
  project_invitation: "invites",
  invite: "invites",
  user: "account",
  flag: "flags",
  message: "messages",
  session: "sessions",
  reward: "rewards",
  xp: "rewards",
  blog: "blogs",
  photo: "photography",
  photography: "photography",
}

/** Normalize API subject_type → admin tab key. */
const normalizeAdminSubjectType = log => {
  const raw = String(log?.subject_type || "")
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .toLowerCase()
    .trim()

  if (!raw) {
    const logName = String(log?.log_name || "").toLowerCase()
    if (logName === "loggedin" || logName === "loggedout") return "user"
    return ""
  }

  if (raw === "user") return "user"
  if (raw === "message") return "message"
  if (raw === "flag") return "flag"
  if (raw === "reward" || raw === "xp") return "reward"
  if (raw === "project" || raw.startsWith("project_")) return "project"

  return ""
}

const humanizeKey = value =>
  String(value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase())

const isPresent = value =>
  value !== null && value !== undefined && String(value).trim() !== ""

const getLogEntity = properties => {
  if (properties?.new && typeof properties.new === "object") return properties.new
  if (properties?.old && typeof properties.old === "object") return properties.old
  return properties || {}
}

const inferModuleKey = log => {
  const logName = String(log?.log_name || "").toLowerCase()
  if (logName === "loggedin" || logName === "loggedout") return "account"

  const subject = String(log?.subject_type || "")
    .replace(/\\/g, "/")
    .split("/")
    .pop()
    .toLowerCase()
  if (SUBJECT_MODULE[subject]) return SUBJECT_MODULE[subject]

  const haystack = [
    log?.log_name,
    log?.properties?.event,
    log?.subject_type,
    log?.description,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()

  if (/flag|report|safety/.test(haystack)) return "flags"
  if (/message|chat|dm/.test(haystack)) return "messages"
  if (/reward|xp|experience/.test(haystack)) return "rewards"
  if (/invite/.test(haystack)) return "invites"
  if (/session/.test(haystack)) return "sessions"
  if (/call for|calls for/.test(haystack)) return "calls"
  if (/photo|camera/.test(haystack)) return "photography"
  if (/blog/.test(haystack)) return "blogs"
  if (/password|account|logged ?in|logged ?out|login|logout/.test(haystack)) {
    return "account"
  }
  if (/profile/.test(haystack)) return "profile"
  if (/project/.test(haystack)) return "projects"
  return "projects"
}

const buildLogDetails = (log, properties) => {
  if (isPresent(properties.details)) return stripHtml(properties.details)
  if (isPresent(properties.subtitle)) return stripHtml(properties.subtitle)
  if (isPresent(properties.subject)) return stripHtml(properties.subject)
  if (isPresent(properties.project_name)) return stripHtml(properties.project_name)
  if (isPresent(properties.target)) return stripHtml(properties.target)

  const entity = getLogEntity(properties)
  const logName = String(log?.log_name || "").toLowerCase()
  if (logName === "loggedin" || logName === "loggedout") {
    return (
      entity.email ||
      properties.email ||
      entity.full_name ||
      "—"
    )
  }

  const old = properties.old && typeof properties.old === "object" ? properties.old : {}
  const changed =
    properties.changed && typeof properties.changed === "object"
      ? properties.changed
      : {}

  if (isPresent(entity.title)) return stripHtml(entity.title)
  if (isPresent(entity.name)) return stripHtml(entity.name)
  if (isPresent(entity.full_name)) return stripHtml(entity.full_name)
  if (isPresent(entity.body)) return stripHtml(entity.body)

  const changeKeys = Object.keys(changed).filter(key => key !== "updated_at")
  if (changeKeys.length > 0) {
    return changeKeys
      .map(key => {
        const from = old[key]
        const to = changed[key]
        const label = humanizeKey(key)
        if (isPresent(from) && isPresent(to) && String(from) !== String(to)) {
          return `${label}: ${stripHtml(from)} → ${stripHtml(to)}`
        }
        return `${label}: ${stripHtml(to)}`
      })
      .join(", ")
  }

  if (isPresent(entity.email)) return entity.email
  if (isPresent(properties.email)) return properties.email
  if (isPresent(entity.status)) return humanizeKey(entity.status)
  if (isPresent(entity.role)) return humanizeKey(entity.role)

  const description = stripHtml(log?.description || "")
  const colon = description.indexOf(":")
  if (colon !== -1) {
    const rest = description.slice(colon + 1).trim()
    if (rest && rest.toLowerCase() !== "unknown") return rest
  }

  return "—"
}

const buildLogSubtitle = (log, properties, { isAdmin = false } = {}) => {
  if (isPresent(properties.subtitle)) return stripHtml(properties.subtitle)
  if (isPresent(properties.summary)) return stripHtml(properties.summary)

  const actor =
    properties.full_name ||
    [properties.first_name, properties.last_name].filter(Boolean).join(" ").trim() ||
    properties.email ||
    ""
  const who = isAdmin ? actor || "Someone" : "You"

  const logName = String(log?.log_name || "").toLowerCase()
  if (logName === "loggedin") {
    return isAdmin
      ? `${who} logged into their account`
      : "You logged into your account"
  }
  if (logName === "loggedout") {
    return isAdmin
      ? `${who} logged out of their account`
      : "You logged out of your account"
  }

  const event = String(properties.event || log?.event || "").toLowerCase()
  const subject = humanizeKey(log?.subject_type).toLowerCase() || "record"

  if (event === "created") return `${who} created a ${subject}`
  if (event === "updated") return `${who} updated a ${subject}`
  if (event === "deleted") return `${who} deleted a ${subject}`

  if (log?.log_name) {
    return `${who} ${humanizeKey(log.log_name).toLowerCase()}`
  }
  return ""
}

const formatDateTime = value => {
  if (!value) return "—"
  const date = dayjs(value)
  return date.isValid() ? date.format("MMM D, YYYY h:mm A") : "—"
}

const enrichLog = log => {
  const moduleKey = log.moduleKey || inferModuleKey(log)
  const meta = MODULE_META[moduleKey] || MODULE_META.projects
  return {
    ...log,
    moduleKey,
    tab: meta.tab,
    moduleLabel: meta.label,
    tone: meta.tone,
    icon: log.icon || meta.icon,
    dateLabel: formatDateTime(log.createdAt),
    dateValue: log.createdAt,
  }
}

const extractLogList = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const fetchActivityLogsPage = ({
  isAdmin,
  page,
  pageSize,
  search = "",
  sortBy = DEFAULT_SORT_BY,
  sortDirection = "desc",
  cityId = "",
  subjectType = "",
}) => {
  const endpoint = isAdmin
    ? endpoints.getAdminActivityLogs
    : endpoints.getMyActivityLogs

  const params = {
    page,
    page_size: pageSize,
    sort_by: sortBy || DEFAULT_SORT_BY,
    sort_direction: sortDirection,
  }

  if (search) {
    params.search = search
  }

  if (isAdmin && cityId) {
    params.city_id = cityId
  }

  if (subjectType && subjectType !== "all") {
    params.subject_type = subjectType
  }

  return get(endpoint, {
    params,
    skipErrorToast: true,
  })
}

const mapApiLog = (log, { isAdmin = false } = {}) => {
  const properties = log?.properties || {}
  const moduleKey = inferModuleKey(log)
  const subjectType = normalizeAdminSubjectType(log)
  const haystack = [
    log?.log_name,
    log?.description,
    properties.event,
    log?.subject_type,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()

  let icon
  if (/publish/.test(haystack) && /project/.test(haystack)) {
    icon = ICONS.activityMap
  } else if (/\bxp\b/.test(haystack) && /earn/.test(haystack)) {
    icon = ICONS.activityTicketStar
  }

  const actor =
    properties.full_name ||
    [properties.first_name, properties.last_name].filter(Boolean).join(" ").trim() ||
    ""
  const description = stripHtml(
    log.description || humanizeKey(log.log_name) || "Activity",
  )

  const enriched = enrichLog({
    id: log.id,
    title:
      isAdmin && actor && !properties.new && !properties.old
        ? `${actor} — ${description}`
        : description,
    subtitle: buildLogSubtitle(log, properties, { isAdmin }),
    details: buildLogDetails(log, properties),
    moduleKey,
    icon,
    createdAt: log.created_at,
    subjectType,
  })

  if (subjectType) {
    return {
      ...enriched,
      title: stripHtml(enriched.title),
      tab: subjectType,
      moduleLabel: TAB_LABELS[subjectType] || enriched.moduleLabel,
    }
  }

  return {
    ...enriched,
    title: stripHtml(enriched.title),
  }
}

const ActivityLog = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isAdminLayout = location.pathname.startsWith("/admin/activity-log")
  /** Admin chrome (AdminLayout) vs member VerticalLayout. */
  const isAdminShell = isAdminLayout
  const cityIdFilter = isAdminLayout
    ? searchParams.get("city_id")?.trim() || ""
    : ""
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY)
  const [sortDirection, setSortDirection] = useState("desc")
  const [currentPage, setCurrentPage] = useState(1)
  const [logs, setLogs] = useState([])
  const [serverTotal, setServerTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    )
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, cityIdFilter, debouncedSearch, sortBy, sortDirection])

  const fetchLogs = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await fetchActivityLogsPage({
        isAdmin: isAdminLayout,
        page: currentPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        sortBy,
        sortDirection,
        cityId: cityIdFilter,
        subjectType: activeTab,
      })

      if (!response?.data?.success) {
        setLogs([])
        setServerTotal(0)
        return
      }

      const result = response.data.result || {}
      const rows = extractLogList(result).map(log =>
        mapApiLog(log, { isAdmin: isAdminLayout }),
      )
      setLogs(rows)
      setServerTotal(Number(result.total) || rows.length)
    } catch {
      setLogs([])
      setServerTotal(0)
    } finally {
      setIsLoading(false)
    }
  }, [
    activeTab,
    cityIdFilter,
    currentPage,
    debouncedSearch,
    isAdminLayout,
    sortBy,
    sortDirection,
  ])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const tabCounts = useMemo(() => {
    const counts = {}
    // Only the active tab has a known total from the current page fetch.
    // Omit inactive tabs so ListingTabs does not show a default "0".
    if (Number.isFinite(serverTotal) && serverTotal > 0) {
      counts[activeTab] = serverTotal
    }
    return counts
  }, [activeTab, serverTotal])

  const totalItems = serverTotal
  const pagedLogs = logs

  const tabItems = TAB_KEYS.map(key => {
    const count = tabCounts[key]
    if (typeof count !== "number" || count <= 0) {
      return { key, label: TAB_LABELS[key] }
    }
    return { key, label: TAB_LABELS[key], count }
  })

  const handleTabChange = key => {
    setActiveTab(key)
    setCurrentPage(1)
  }

  const handleSubjectTypeFilterChange = next => {
    const value = Array.isArray(next)
      ? String(next[0] || "")
      : String(next || "")
    setActiveTab(value || "all")
    setCurrentPage(1)
  }

  const handleExport = async () => {
    if (!isAdminLayout || isExporting) return
    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminActivityLogs, {
        params: {
          search: debouncedSearch,
          sort_by: sortBy || DEFAULT_SORT_BY,
          sort_direction: sortDirection,
          ...(cityIdFilter ? { city_id: cityIdFilter } : {}),
          ...(activeTab !== "all" ? { subject_type: activeTab } : {}),
        },
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export activity log")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export activity log"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default message
        }
        toast.error(message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `activity-log-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Activity log exported.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
    } catch (err) {
      toastApiError(err, "Failed to export activity log")
    } finally {
      setIsExporting(false)
    }
  }

  const pageTitle = isAdminLayout ? "Admin Logs" : "Member Activity Log"
  const pageSubtitle = isAdminLayout
    ? cityIdFilter
      ? "Filtered activity for the selected city"
      : "A record of platform activity across AMOK.EARTH"
    : "A record of your activity across AMOK.EARTH"

  return (
    <div
      className={
        isAdminShell ? "admin-page admin-activity-log" : "page-content"
      }
    >
      <div
        className={`activity-log${
          isAdminShell ? " activity-log--admin" : ""
        }`}
      >
        <PageHeader
          className={`activity-log__header${
            isAdminShell ? " admin-page__header" : ""
          }`}
          title={pageTitle}
          subtitle={pageSubtitle}
          actions={
            <div className="activity-log__actions">
              <SearchInput
                  value={search}
                  onChange={event => {
                    setSearch(event.target.value)
                    setCurrentPage(1)
                  }}
                  placeholder="Search by activity..."
                  iconSrc={ICONS.search}
                  className="activity-log__search"
                />
              <FilterDropdown
                label="Filter"
                filters={[
                  {
                    key: "subject_type",
                    type: "select",
                    label: "Subject Type",
                    multiple: false,
                    defaultValue: "",
                    options: SUBJECT_TYPE_FILTER_OPTIONS,
                    value: activeTab === "all" ? "" : activeTab,
                    onChange: handleSubjectTypeFilterChange,
                  },
                  {
                    key: "sortBy",
                    type: "sortBy",
                    label: "Sort By",
                    value: sortBy,
                    defaultValue: DEFAULT_SORT_BY,
                    onChange: next => {
                      setSortBy(next || DEFAULT_SORT_BY)
                      setCurrentPage(1)
                    },
                    options: SORT_OPTIONS,
                  },
                  {
                    key: "sortDirection",
                    type: "sortDirection",
                    label: "Order",
                    value: sortDirection,
                    defaultValue: "desc",
                    onChange: next => {
                      setSortDirection(next || "desc")
                      setCurrentPage(1)
                    },
                  },
                ]}
              />
              {isAdminLayout ? (
                <CustomButton
                  variant="primary"
                  size="md"
                  type="button"
                  trailingIconSrc={ICONS.activityDocumentDownload}
                  trailingIconAlt=""
                  onClick={handleExport}
                  loading={isExporting}
                  disabled={isExporting}
                >
                  Export Log
                </CustomButton>
              ) : null}
            </div>
          }
        />

        <div className="activity-log__tabs">
          <ListingTabs
            items={tabItems}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </div>

        <div className="activity-log__table-wrapper">
          <div className="activity-log__table" aria-busy={isLoading || undefined}>
            <div className="activity-log__row activity-log__row--head">
              <div>Activity</div>
              <div>Module</div>
              <div>Details</div>
              <div>Date & Time</div>
            </div>

            {isLoading &&
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="activity-log__row activity-log__row--skeleton"
                  aria-hidden="true"
                >
                  {Array.from({ length: 4 }).map((__, cellIdx) => (
                    <div key={cellIdx} className="activity-log__skeleton-cell">
                      <div className="activity-log__skeleton-bar" />
                    </div>
                  ))}
                </div>
              ))}

            {!isLoading &&
              pagedLogs.map(log => (
                <div key={log.id} className="activity-log__row">
                  <div className="activity-log__activity">
                    <span
                      className={`activity-log__icon activity-log__icon--${log.tone}`}
                    >
                      <img src={log.icon} alt="" width={18} height={18} />
                    </span>
                    <span className="activity-log__activity-text">
                      <TruncatedText className="activity-log__title">
                        {log.title}
                      </TruncatedText>
                      <TruncatedText className="activity-log__subtitle">
                        {log.subtitle}
                      </TruncatedText>
                    </span>
                  </div>
                  <div className="activity-log__module">
                    <span
                      className={`activity-log__tag activity-log__tag--${log.tone}`}
                    >
                      {log.moduleLabel}
                    </span>
                  </div>
                  <div className="activity-log__details">
                    <TruncatedText>{log.details}</TruncatedText>
                  </div>
                  <div className="activity-log__date">
                    <TruncatedText>{log.dateLabel}</TruncatedText>
                  </div>
                </div>
              ))}

            {!isLoading && pagedLogs.length === 0 && (
              <EmptyState
                title={
                  search || activeTab !== "all"
                    ? "No Matching Activity"
                    : "No Activity Yet"
                }
                description={
                  search || activeTab !== "all"
                    ? "Try a different search or clear your filters."
                    : isAdminLayout
                      ? "Platform activity across AMOK.EARTH will show up here."
                      : "Your activity across AMOK.EARTH will show up here."
                }
              />
            )}
          </div>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          itemLabel="Activities"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />
      </div>
    </div>
  )
}

export default ActivityLog
